import { formatCurrency } from '@eriko/shared-types';
import { Clock } from 'lucide-react';

interface Row {
  vendorId: string;
  name: string;
  current: number;
  bucket1_30: number;
  bucket31_60: number;
  bucket61_90: number;
  bucket90Plus: number;
  total: number;
}

interface Totals {
  current: number;
  bucket1_30: number;
  bucket31_60: number;
  bucket61_90: number;
  bucket90Plus: number;
  grandTotal: number;
}

interface Data {
  type: string;
  asOfDate: string;
  currency?: string;
  vendors: Row[];
  totals: Totals;
}

export function ApAgingReport({ data }: { data: Record<string, unknown> }) {
  const d = data as unknown as Data;
  const currency = d.currency ?? 'USD'; // home currency for correct symbol (REPFX-1/CUR-1)

  return (
    <div className="overflow-hidden rounded-[10px] border border-surface-200 bg-surface-0 transition-shadow hover:shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
      <div className="flex items-center justify-between border-b border-surface-100 px-4.5 py-3.5">
        <div className="flex items-center gap-2.5">
          <Clock className="h-4.25 w-4.25 text-surface-400" strokeWidth={1.5} />
          <span className="text-sm font-semibold text-surface-900">Accounts Payable Aging</span>
        </div>
        <span className="rounded-full bg-surface-50 px-2.5 py-0.75 text-xs font-medium text-surface-500">
          as of {d.asOfDate}
        </span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-surface-50 text-[12px] uppercase tracking-[0.04em] text-surface-500">
              <th className="px-4.5 py-2 text-left font-medium">Vendor</th>
              <th className="px-3 py-2 text-right font-medium">Current</th>
              <th className="px-3 py-2 text-right font-medium">1–30</th>
              <th className="px-3 py-2 text-right font-medium">31–60</th>
              <th className="px-3 py-2 text-right font-medium">61–90</th>
              <th className="px-3 py-2 text-right font-medium">90+</th>
              <th className="px-4.5 py-2 text-right font-medium">Total</th>
            </tr>
          </thead>
          <tbody>
            {d.vendors.map((v) => (
              <tr key={v.vendorId} className="border-t border-surface-50 hover:bg-surface-50">
                <td className="truncate px-4.5 py-1.75 text-surface-700">{v.name}</td>
                <td className="px-3 py-1.75 text-right font-mono tabular-nums">
                  {formatCurrency(v.current, 2, currency)}
                </td>
                <td className="px-3 py-1.75 text-right font-mono tabular-nums">
                  {formatCurrency(v.bucket1_30, 2, currency)}
                </td>
                <td className="px-3 py-1.75 text-right font-mono tabular-nums">
                  {formatCurrency(v.bucket31_60, 2, currency)}
                </td>
                <td className="px-3 py-1.75 text-right font-mono tabular-nums">
                  {formatCurrency(v.bucket61_90, 2, currency)}
                </td>
                <td
                  className={`px-3 py-1.75 text-right font-mono tabular-nums${v.bucket90Plus > 0 ? ' text-destructive font-semibold' : ''}`}
                >
                  {formatCurrency(v.bucket90Plus, 2, currency)}
                </td>
                <td className="px-4.5 py-1.75 text-right font-mono font-semibold tabular-nums">
                  {formatCurrency(v.total, 2, currency)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-surface-200 bg-surface-50 font-semibold">
              <td className="px-4.5 py-2 text-surface-900">Total</td>
              <td className="px-3 py-2 text-right font-mono tabular-nums">
                {formatCurrency(d.totals.current, 2, currency)}
              </td>
              <td className="px-3 py-2 text-right font-mono tabular-nums">
                {formatCurrency(d.totals.bucket1_30, 2, currency)}
              </td>
              <td className="px-3 py-2 text-right font-mono tabular-nums">
                {formatCurrency(d.totals.bucket31_60, 2, currency)}
              </td>
              <td className="px-3 py-2 text-right font-mono tabular-nums">
                {formatCurrency(d.totals.bucket61_90, 2, currency)}
              </td>
              <td className="px-3 py-2 text-right font-mono tabular-nums">
                {formatCurrency(d.totals.bucket90Plus, 2, currency)}
              </td>
              <td className="px-4.5 py-2 text-right font-mono tabular-nums">
                {formatCurrency(d.totals.grandTotal, 2, currency)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
